import React from 'react';
import { Area } from '../types';
import { Users, Briefcase, ChevronRight, Shield, Cloud, Settings, Code, Database, Cog, Zap, Target, GitBranch } from 'lucide-react';

interface AreaCardProps {
  area: Area;
  layoutMode: 'grid' | 'list';
  onClick?: () => void;
}

const AreaCard: React.FC<AreaCardProps> = ({ area, layoutMode, onClick }) => {
  const cardClasses = layoutMode === 'grid'
    ? "card cursor-pointer p-6 transition-all"
    : "card cursor-pointer p-4 transition-all flex items-center";

  const getAreaIcon = (areaName: string) => {
    const name = areaName.toLowerCase();

    if (name.includes('secure') || name.includes('segur') || name.includes('security'))
      return <Shield size={20} />;
    if (name.includes('cloud') || name.includes('azure') || name.includes('aws'))
      return <Cloud size={20} />;
    if (name.includes('code') || name.includes('development') || name.includes('desarrollo') || name.includes('dev'))
      return <Code size={20} />;
    if (name.includes('data') || name.includes('datos') || name.includes('analytics'))
      return <Database size={20} />;
    if (name.includes('service') || name.includes('servicio') || name.includes('support') || name.includes('soporte'))
      return <Settings size={20} />;
    if (name.includes('delivery') || name.includes('entrega') || name.includes('operations') || name.includes('operaciones'))
      return <Cog size={20} />;
    if (name.includes('empowering') || name.includes('humans') || name.includes('personas') || name.includes('rrhh') || name.includes('hr'))
      return <Users size={20} />;
    if (name.includes('change') || name.includes('cambio') || name.includes('management') || name.includes('gestión'))
      return <GitBranch size={20} />;
    if (name.includes('quality') || name.includes('calidad') || name.includes('compliance') || name.includes('cumplimiento'))
      return <Target size={20} />;
    if (name.includes('innovation') || name.includes('innovación') || name.includes('empower'))
      return <Zap size={20} />;

    return <Briefcase size={20} />;
  };

  return (
    <div className={cardClasses} onClick={onClick}>
      {/* Header */}
      <div className={layoutMode === 'grid' ? "flex items-start gap-3 mb-4" : "flex items-center gap-3 flex-1"}>
        {/* Icon */}
        <div
          className="flex-shrink-0 w-10 h-10 rounded-md flex items-center justify-center"
          style={{ color: area.color || '#2563EB' }}
        >
          {getAreaIcon(area.nombre)}
        </div>

        {/* Title and subtitle */}
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-neutral-900 dark:text-neutral-50 mb-0.5">
            {area.nombre}
          </h3>
          <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
            {area.unidadNegocio ? area.unidadNegocio.nombre : 'Sin Unidad de Negocio'}
          </p>
        </div>

        {/* Active status indicator */}
        {area.activa && (
          <div className="flex-shrink-0">
            <div className="w-2 h-2 bg-success rounded-full"></div>
          </div>
        )}
      </div>

      {/* Description */}
      {layoutMode === 'grid' && (
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 line-clamp-2">
          {area.descripcion}
        </p>
      )}

      {layoutMode === 'list' && (
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mx-4 flex-1 line-clamp-1">
          {area.descripcion}
        </p>
      )}

      {/* Statistics */}
      <div className={layoutMode === 'grid'
        ? "flex items-center gap-4 pt-4 border-t border-neutral-200 dark:border-neutral-700"
        : "flex items-center gap-4"
      }>
        <div className="flex items-center gap-2">
          <Briefcase size={14} className="text-neutral-400 dark:text-neutral-500" />
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {area.serviciosCount || 0}
          </span>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            servicios
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Users size={14} className="text-neutral-400 dark:text-neutral-500" />
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {area.personalCount || 0}
          </span>
          <span className="text-xs text-neutral-500 dark:text-neutral-400">
            miembros
          </span>
        </div>
      </div>

      {/* Arrow for list mode */}
      {layoutMode === 'list' && (
        <div className="ml-2">
          <ChevronRight size={20} className="text-neutral-400 dark:text-neutral-500" />
        </div>
      )}
    </div>
  );
};

export default AreaCard;
